import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { CorrespondenceService } from '../../../domain/services/correspondence/correspondence.service';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { DialogService } from '../../../domain/services/dialog/dialog.service';
import { CorrespondenceTemplate } from '../../../domain/models/correspondence.model';
import { CorrespondenceFilesComponent } from '../shared/correspondence-files.component';
import { HtmlEditorComponent } from '../../../common/html-editor/html-editor.component';
import { EmailPreviewComponent, previewShown, setPreviewShown } from '../../../common/html-editor/email-preview.component';
import { InsertVariableComponent } from '../../../common/html-editor/insert-variable.component';

@Component({
  selector: 'app-template-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule,
    MatSlideToggleModule, CorrespondenceFilesComponent, HtmlEditorComponent, EmailPreviewComponent, InsertVariableComponent],
  templateUrl: './template-form.component.html',
})
export class TemplateFormComponent implements OnInit {
  showPreview = previewShown();
  togglePreview(): void { this.showPreview = !this.showPreview; setPreviewShown(this.showPreview); }

  @ViewChild('editor') editor?: HtmlEditorComponent;
  @ViewChild(CorrespondenceFilesComponent, { static: true }) files?: CorrespondenceFilesComponent;

  form: FormGroup;
  id: number | null = null;
  template: CorrespondenceTemplate | null = null;
  tokens: string[] = [];
  saving = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly breadcrumbs: BreadcrumbService,
    private readonly dialog: DialogService,
    private readonly service: CorrespondenceService,
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(150)]],
      subject: ['', [Validators.required, Validators.maxLength(250)]],
      body: ['', Validators.required],
      active: [true],
    });
  }

  ngOnInit(): void {
    this.breadcrumbs.updateBreadcrumbs([
      { icon: 'mail', url: '/app/correspondence/emails', label: 'Correspondence' },
      { icon: '', url: '/app/correspondence/templates', label: 'Message templates' },
    ]);
    this.service.getTokens().subscribe((t) => (this.tokens = t));
    const raw = this.route.snapshot.params['id'];
    if (raw && raw !== 'new') {
      this.id = Number(raw);
      this.service.getTemplate(this.id).subscribe((t) => {
        this.template = t;
        this.form.patchValue({ name: t.name, subject: t.subject, body: t.body, active: t.active });
      });
    }
  }

  insertToken(token: string): void { this.insert(token); }
  insertImage(cid: string): void { this.insert(`<img src="cid:${cid}" alt="" style="max-width:100%">`); }

  private insert(html: string): void { this.editor?.insert(html); }


  save(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    const v = this.form.value;
    this.service.saveTemplate(this.id, { name: v.name, subject: v.subject, body: v.body, active: v.active },
      this.files?.value ?? { keepIds: [], attachments: [], inline: [] }).subscribe({
      next: () => {
        this.saving = false;
        this.dialog.openSuccesDialog('Saved', 'Template saved.');
        this.router.navigate(['/app/correspondence/templates']);
      },
      error: (e) => {
        this.saving = false;
        this.dialog.openErrorDialog('Error', e?.error?.message || 'Could not save the template.');
      },
    });
  }

  goBack(): void { this.router.navigate(['/app/correspondence/templates']); }
}
